import React from 'react';
import styles from './ZipClustersTab.module.css';
import { ZipCluster } from '../routeDensityTypes';

interface ZipClustersTabProps {
  clusters: ZipCluster[];
  orphanCluster: ZipCluster;
}

const LocationIcon: React.FC<{ isOrphan?: boolean }> = ({ isOrphan }) => (
  <svg className={isOrphan ? styles.pinOrphan : styles.pinNormal} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
  </svg>
);

const ClusterCard: React.FC<{ cluster: ZipCluster }> = ({ cluster }) => {
  const isOrphan = cluster.isOrphan;

  return (
    <div className={`${styles.card} ${isOrphan ? styles.cardOrphan : ''}`}>
      <div className={styles.cardHeader}>
        <LocationIcon isOrphan={isOrphan} />
        <div>
          <p className={styles.clusterName}>{cluster.name}</p>
          <p className={styles.clusterZips}>{cluster.zipCodes}</p>
        </div>
      </div>
      <div className={styles.statsRow}>
        <div className={styles.statItem}>
          <span className={styles.statLabel}>Stores</span>
          <span className={styles.statValue}>{cluster.stores}</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statLabel}>Weekly Revenue</span>
          <span className={`${styles.statValue} ${styles.revenueValue}`}>{cluster.weeklyRevenue}</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statLabel}>Avg/Store</span>
          <span className={styles.statValue}>{cluster.avgPerStore}</span>
        </div>
      </div>
      {isOrphan && cluster.warning && (
        <div className={styles.orphanWarning}>
          <span className={styles.warningIcon}>⚠️</span>
          <div>
            <p className={styles.warningTitle}>Orphan stores (no assigned route)</p>
            <p className={styles.warningText}>{cluster.warning}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export const ZipClustersTab: React.FC<ZipClustersTabProps> = ({ clusters, orphanCluster }) => {
  return (
    <div>
      {/* Header */}
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        </div>
        <div>
          <h3 className={styles.sectionTitle}>Geographic Clustering Analysis</h3>
          <p className={styles.sectionSubtitle}>ZIP code clusters show route density opportunities. Consolidate nearby stores for better efficiency.</p>
        </div>
      </div>

      {/* Clusters Grid */}
      <div className={styles.grid}>
        {clusters.map((cluster) => (
          <ClusterCard key={cluster.id} cluster={cluster} />
        ))}
      </div>

      {/* Orphan Cluster */}
      <div className={styles.orphanSection}>
        <ClusterCard cluster={orphanCluster} />
      </div>
    </div>
  );
};
